/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useContext, useState } from 'react';
import FieldName from '../utils/FieldName';
import PickColorButton from './PickColorButton';
import { ClickAwayListener } from '@mui/material';
import PickerContent from './PickerContent';
import ColorPreview from './ColorPreview';
import { FormattedMessage } from 'react-intl';
import useApi from '@/lib/api/useApi';
import PortfolioItemContext from '../../../../PortfolioItemContext';
import useIsPortfolioReadonly from '@/components/ChallengePortfolio/NormalView/useIsPortfolioReadonly';
import {
  type ChallengeWithoutRelations,
  type TaskWithoutRelations,
} from '@/lib/types';
import useComplexSwrState from '@/lib/utils/useComplexSwrState';
import PickerWrapper from './PickerWrapper';
import useBlockPortfolioItemCloseWhenDirty from '@/components/ChallengePortfolio/NormalView/portfolio-item/useBlockPortfolioItemCloseWhenDirty';

const ColorPicker = () => {
  const api = useApi();

  const {
    portfolioItem,
    setPortfolioItem,
    setPortfolioItemSummary,
    portfolioItemApiEndpoint,
  } = useContext(PortfolioItemContext);

  const portfolioIsReadonly = useIsPortfolioReadonly();

  const [
    selectedColor,
    setSelectedColor,
    selectedColorHasChanged,
    reenableSelectedColorSync,
  ] = useComplexSwrState<string | null>(portfolioItem.color);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSave = useCallback(async (): Promise<void> => {
    if (!pickerOpen) {
      return;
    }

    setPickerOpen(false);

    if (portfolioItem.color === selectedColor) {
      return;
    }

    setSaving(true);

    const updatedPortfolioItem = await api.put<
      ChallengeWithoutRelations | TaskWithoutRelations
    >(`${portfolioItemApiEndpoint}/color`, {
      color: selectedColor,
    });

    setPortfolioItem(portfolioItem => {
      portfolioItem.color = updatedPortfolioItem.color;
    });

    setPortfolioItemSummary(portfolioItemSummary => {
      portfolioItemSummary.color = updatedPortfolioItem.color;
    });

    setSaving(false);

    reenableSelectedColorSync();
  }, [pickerOpen, portfolioItem.color, selectedColor]);

  const handleButtonClick = (): void => {
    if (!pickerOpen) {
      setPickerOpen(true);

      return;
    }

    handleSave();
  };

  useBlockPortfolioItemCloseWhenDirty(selectedColorHasChanged, handleSave);

  return (
    <section>
      <FieldName />
      <ClickAwayListener onClickAway={handleSave}>
        <PickerWrapper>
          <PickColorButton
            color="secondary"
            onClick={handleButtonClick}
            disabled={portfolioIsReadonly || saving}
          >
            <ColorPreview selectedColor={selectedColor} />
            <FormattedMessage id="portfolio.challenge.color_picker.button" />
          </PickColorButton>
          {pickerOpen && (
            <PickerContent
              selectedColor={selectedColor}
              setSelectedColor={setSelectedColor}
            />
          )}
        </PickerWrapper>
      </ClickAwayListener>
    </section>
  );
};

export default ColorPicker;
